import { injectDialog } from '@modules/components/dialog/inject-dialog';
import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { PASSWORD_RULES } from '@constants/password-rules';
import { ButtonComponent } from '@modules/components/button/button.component';
import { CardComponent } from '@modules/components/card/card.component';
import { USER } from '@tokens/user.token';
import { UserType, UserUpdateRequest } from '@type/user.type';
import { firstValueFrom, switchMap, tap } from 'rxjs';
import { UserService } from 'src/app/core/services/user/user.service';
import { SqlQueryTokenDialogComponent } from './components/sql-query-token-dialog/sql-query-token-dialog.component';
import { UserProfileFormService } from './services/user-form/user-profile-form.service';
import { FormFieldComponent } from '@modules/components/form-field/form-field.component';

@Component({
  selector: 'app-user-profile',
  imports: [FormFieldComponent, MatToolbarModule, ReactiveFormsModule, MatIconModule, CardComponent, ButtonComponent],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class UserProfileComponent {
  #userService = inject(UserService);
  #user = inject(USER);
  #userForm = inject(UserProfileFormService);

  #dialog = injectDialog();

  form = this.#userForm.form;

  passwordVisibility = {
    current: false,
    new: false,
    confirm: false,
  };

  passwordRules = PASSWORD_RULES;

  availableCompanyList = computed(() => this.#user().available_company_ids);
  emptyAvailableCompanyList = computed(() => !this.availableCompanyList().length && this.#user().superUserRole);
  showAvailableCompanyList = computed(() => this.availableCompanyList().length && this.#user().superUserRole);

  hasSqlAccess = computed(() => this.#user().sql_query_access === true);

  hasSqlToken = computed(() => this.#user().has_sql_query_token === true);

  get isSaveDisabled(): boolean {
    const mainFormInvalid = this.form.get('firstName')?.invalid || this.form.get('lastName')?.invalid;
    const password = this.form.get('password')?.value;
    const passwordForm = this.form.get('passwordForm') as FormGroup;
    const newPassword = passwordForm.get('newPassword')?.value;
    const confirmPassword = passwordForm.get('confirmPassword')?.value;

    const anyPasswordFieldFilled = !!password || !!newPassword || !!confirmPassword;

    const passwordFieldsInvalid =
      anyPasswordFieldFilled && (this.form.get('password')?.invalid || passwordForm.invalid);

    return !!mainFormInvalid || !!passwordFieldsInvalid;
  }

  constructor() {
    effect(() => {
      const user = this.#user();
      this.form.patchValue(this.#prepareToFront(user));
    });
  }

  reset() {
    this.form.reset(this.#prepareToFront(this.#user()));
  }

  updateUser() {
    return firstValueFrom(
      this.#userService.updateCurrentUser(this.#updateRequestUser()).pipe(
        tap(() => {
          this.reset();
          this.form.markAsUntouched();
        }),
      ),
    );
  }

  togglePasswordVisibility(field: 'current' | 'new' | 'confirm') {
    this.passwordVisibility[field] = !this.passwordVisibility[field];
  }

  createToken() {
    firstValueFrom(
      this.#userService.createSqlQueryToken().pipe(
        tap(({ token }) => this.#dialog.open(SqlQueryTokenDialogComponent, { data: token, width: '480px' })),
        switchMap(() => this.#userService.getCurrentUser()),
      ),
    );
  }

  reissueToken() {
    if (!confirm('Previous token will stop working. Continue?')) return;
    firstValueFrom(
      this.#userService.createSqlQueryToken().pipe(
        tap(({ token }) => this.#dialog.open(SqlQueryTokenDialogComponent, { data: token, width: '480px' })),
        switchMap(() => this.#userService.getCurrentUser()),
      ),
    );
  }

  revokeToken() {
    if (!confirm('Revoke the token? It will stop working immediately.')) return;
    firstValueFrom(this.#userService.revokeSqlQueryToken().pipe(switchMap(() => this.#userService.getCurrentUser())));
  }

  #prepareToFront(user: UserType) {
    return {
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      role: user.role.name,
    };
  }

  #updateRequestUser(): UserUpdateRequest {
    return new UserUpdateRequest(this.#userForm);
  }
}
